import { type SavedMapPlotRequestDetail } from "../../types";
import {
  SAVED_PLOT_LIST_COLORS,
  getSavedPlotListColor,
} from "../canvasPin/teardropPin";

/** 地図に出している保存した物件リスト（画面上部の「表示中のプロットデータ」に並ぶもの） */
export interface LoadedDataset {
  id: number;
  name: string;
  /** このリストのピンの色 */
  color: string;
}

interface DatasetInfo {
  id: number;
  name: string;
}

/** 地図に出しているリストとそのピン */
interface LoadedState {
  datasets: LoadedDataset[];
  plots: SavedMapPlotRequestDetail[];
}

/**
 * まだ使っていない色を先頭から選ぶ。全部使っていれば出している数で繰り返す
 * （外したリストの色を、次に足したリストが使えるようにするため）
 */
const pickListColor = (datasets: readonly LoadedDataset[]): string =>
  SAVED_PLOT_LIST_COLORS.find(
    (color) => !datasets.some((dataset) => dataset.color === color)
  ) ?? getSavedPlotListColor(datasets.length);

/**
 * 地図に出しているリストの後ろに、新しく選んだリストを足す。
 * 色は足した時に決めて持ち回り、あとでほかのリストを外しても変えない。
 * すでに出しているリストは足さない
 */
export const appendLoadedDatasets = (
  current: readonly LoadedDataset[],
  added: readonly DatasetInfo[]
): LoadedDataset[] =>
  added.reduce<LoadedDataset[]>(
    (datasets, { id, name }) =>
      datasets.some((dataset) => dataset.id === id)
        ? datasets
        : [...datasets, { id, name, color: pickListColor(datasets) }],
    [...current]
  );

/**
 * 「地図に反映する」で選んだリストを、いま出しているリストに足す。
 * いま出しているリストに属さないピン（物件リストの詳細画面から1件だけ出したものなど）は
 * 「表示中のプロットデータ」から外せないので、これまでどおり入れ替える
 */
export const addLoadedDatasets = (
  current: LoadedState,
  added: {
    datasets: readonly DatasetInfo[];
    plots: readonly SavedMapPlotRequestDetail[];
  }
): LoadedState => {
  const currentIds = new Set(current.datasets.map((dataset) => dataset.id));
  const belongsToCurrent = (plot: SavedMapPlotRequestDetail): boolean =>
    currentIds.has(plot.mapPlotRequestId);

  return {
    datasets: appendLoadedDatasets(current.datasets, added.datasets),
    plots: [
      ...current.plots.filter(belongsToCurrent),
      ...added.plots.filter((plot) => !belongsToCurrent(plot)),
    ],
  };
};

/** 物件リストID → ピンの色 */
export const toColorByRequestId = (
  datasets: readonly LoadedDataset[]
): ReadonlyMap<number, string> =>
  new Map(datasets.map(({ id, color }) => [id, color]));

/**
 * 指定したリストを地図から外す（保存した物件リスト自体は消さない）。
 * そのリストのピンと、画面上部のリスト名の両方から除く
 */
export const removeLoadedDataset = (
  current: LoadedState,
  requestId: number
): LoadedState => ({
  datasets: current.datasets.filter((dataset) => dataset.id !== requestId),
  plots: current.plots.filter((plot) => plot.mapPlotRequestId !== requestId),
});
